/**
 * 响应式筛选面板组件
 * 
 * 功能：
 * - 移动端：使用Drawer抽屉式筛选
 * - 桌面端：使用Collapse可折叠面板
 * - 状态持久化：记住展开/收起状态
 */

import React, { useState, useEffect } from 'react';
import { Drawer, Collapse, Button, Card } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '../../hooks/useResponsive';
import { UnifiedFilters } from '../unified';
import { QuestionnaireFilters } from '../../types/questionnaire';
import { EMERALD_THEME } from '../../config/designTokens';

interface ResponsiveFilterPanelProps {
  filters: QuestionnaireFilters;
  filterConfigs: any[];
  onFiltersChange: (key: string, value: any) => void;
  onClearFilters: () => void;
  channelsData?: any;
}

const STORAGE_KEY = 'screening_workbench_filter_collapsed';

export const ResponsiveFilterPanel: React.FC<ResponsiveFilterPanelProps> = ({
  filters,
  filterConfigs,
  onFiltersChange,
  onClearFilters,
  channelsData,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    // 从localStorage读取折叠状态
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  // 保存折叠状态到localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  const handleDrawerClose = () => {
    setDrawerVisible(false);
  };

  const handleCollapseChange = (keys: string | string[]) => {
    setCollapsed(Array.isArray(keys) ? keys.length === 0 : !keys);
  };

  // 移动端：Drawer抽屉
  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={() => setDrawerVisible(true)}
          size="large"
          style={{
            width: '100%',
            marginBottom: EMERALD_THEME.spacing.md,
            borderRadius: EMERALD_THEME.borderRadius.lg,
            height: '48px',
            fontSize: '16px',
            fontWeight: 600
          }}
        >
          {t('screening:filter.smartFilterTitle')}
        </Button>

        <Drawer
          title={t('screening:filter.smartFilterTitle')}
          placement="left"
          width={Math.min(window.innerWidth * 0.85, 360)}
          open={drawerVisible}
          onClose={handleDrawerClose}
          styles={{
            body: {
              padding: EMERALD_THEME.spacing.md
            }
          }}
        >
          <UnifiedFilters
            filters={filters}
            filterConfigs={filterConfigs}
            onFiltersChange={onFiltersChange}
            onClearFilters={onClearFilters}
            variant="embedded"
          />
          
          <Button
            type="primary"
            block
            size="large"
            onClick={handleDrawerClose}
            style={{
              marginTop: EMERALD_THEME.spacing.lg,
              borderRadius: EMERALD_THEME.borderRadius.lg,
              height: '48px',
              fontWeight: 600
            }}
          >
            {t('screening:filter.applyFilter')}
          </Button>
        </Drawer>
      </>
    );
  }

  // 桌面端：Collapse可折叠面板
  return (
    <Card
      style={{
        marginBottom: EMERALD_THEME.spacing.lg,
        borderRadius: EMERALD_THEME.borderRadius.lg,
        border: `1px solid ${EMERALD_THEME.colors.functional.border}`,
        boxShadow: EMERALD_THEME.shadows.sm
      }}
      bodyStyle={{ padding: 0 }}
    >
      <Collapse
        activeKey={collapsed ? [] : ['filters']}
        onChange={handleCollapseChange}
        style={{
          background: 'transparent',
          border: 'none'
        }}
        items={[
          {
            key: 'filters',
            label: (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: EMERALD_THEME.spacing.sm
              }}>
                <FilterOutlined style={{ 
                  color: '#10b981',
                  fontSize: '16px'
                }} />
                <span style={{ 
                  fontSize: '16px',
                  fontWeight: 600
                }}>
                  {t('screening:filter.smartFilterTitle')}
                </span>
              </div>
            ),
            children: (
              <div style={{ padding: `0 ${EMERALD_THEME.spacing.lg}px ${EMERALD_THEME.spacing.lg}px` }}>
                <UnifiedFilters
                  filters={filters}
                  filterConfigs={filterConfigs}
                  onFiltersChange={onFiltersChange}
                  onClearFilters={onClearFilters}
                  variant="embedded"
                />
              </div>
            )
          }
        ]}
      />
    </Card>
  );
};

export default ResponsiveFilterPanel;

